<template>
    <div class="full-height json-formatter-container">

        <div class="left-panel">
            <el-card class="box-card">
                <div slot="header" style="display: flex; justify-content: space-between; align-items: center;">
                    <span>JSON 输入</span>
                    <div style="display: flex;gap:8px">
                        <el-button type="primary" @click="handleFormat" :loading="isProcessing">
                            <i class="el-icon-magic-stick"></i> 格式化
                        </el-button>
                        <el-button @click="handleCompress" :loading="isProcessing">
                            <i class="el-icon-remove"></i> 压缩
                        </el-button>
                        <el-button @click="handleCopy" :disabled="!formModel.resultJson">
                            <i class="el-icon-copy-document"></i> 复制
                        </el-button>
                        <el-button @click="handleClear">
                            <i class="el-icon-delete"></i> 清空
                        </el-button>
                    </div>
                </div>

                <el-input
                    type="textarea"
                    :rows="33"
                    placeholder="请输入JSON字符串内容"
                    v-model="formModel.content"
                    resize="none"
                    class="json-input">
                </el-input>
            </el-card>
        </div>

        <div class="right-panel">
            <el-card class="box-card ">
                <div slot="header" style="display: flex; justify-content: space-between; align-items: center;">
                    <span>JSON 可视化</span>
                    <div class="header-actions">
                        <el-button size="mini" @click="handleCopyResult" :disabled="!formModel.resultJson">
                            <i class="el-icon-copy-document"></i> 复制
                        </el-button>
                        <el-button size="mini" @click="expandAll" :disabled="!formModel.resultJson">
                            <i class="el-icon-plus"></i> 全部展开
                        </el-button>
                        <el-button size="mini" @click="collapseAll" :disabled="!formModel.resultJson">
                            <i class="el-icon-minus"></i> 全部收起
                        </el-button>
                    </div>
                </div>
                <div class="json-viewer-container">
                    <div v-if="!formModel.resultJson" class="empty-state">
                        <i class="el-icon-document"></i>
                        <p>请输入JSON字符串并点击格式化</p>
                    </div>
                    <div v-else class="json-content" :class="{ loading: isProcessing }">
                        <div class="json-scroll-wrapper" ref="jsonScrollWrapper">
                            <vue-json-pretty
                                ref="jsonViewer"
                                virtual
                                show-line-number
                                :height="660"
                                :indent="4"
                                :data="formModel.resultJson"
                                :show-icon="true"
                                :show-length="true"
                                :deep="expandLevel"
                                :highlight-mouseover="true"
                                :class="[ { 'large-json': isLargeJson }]"
                                style="height: 100%; max-height: 100%; overflow: auto;">
                            </vue-json-pretty>
                        </div>
                    </div>
                </div>

            </el-card>
        </div>

    </div>

</template>

<script>
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';


export default {
    name: "JsonFormatter",
    components: {
        VueJsonPretty,
    },
    data() {
        return {

            formModel: {
                content: '',
                result: '',
                resultJson: null
            },
            isProcessing: false,
            expandLevel: 3
        }
    },
    computed: {
        isLargeJson() {
            if (!this.formModel.resultJson) return false;
            const jsonString = JSON.stringify(this.formModel.resultJson);
            return jsonString.length > 50000; // 50KB
        }
    },

    methods: {
        async handleFormat() {
            if (!this.formModel.content.trim()) {
                this.$message.warning('请输入JSON字符串');
                return;
            }

            this.isProcessing = true;
            try {
                // 使用setTimeout让UI有时间更新loading状态
                await new Promise(resolve => setTimeout(resolve, 100));

                const value = JSON.parse(this.formModel.content);
                const formatted = JSON.stringify(value, null, 4);

                this.$set(this.formModel, 'result', formatted);
                this.$set(this.formModel, 'resultJson', value);

                // 性能优化
                this.optimizeLargeJson(value);

                this.$message.success('格式化成功');
            } catch (e) {
                this.$message.error('JSON格式错误: ' + e.message);
            } finally {
                this.isProcessing = false;
            }
        },

        async handleCompress() {
            if (!this.formModel.content.trim()) {
                this.$message.warning('请输入JSON字符串');
                return;
            }

            this.isProcessing = true;
            try {
                await new Promise(resolve => setTimeout(resolve, 100));

                const value = JSON.parse(this.formModel.content);
                const compressed = JSON.stringify(value);

                this.$set(this.formModel, 'result', compressed);
                this.$set(this.formModel, 'resultJson', value);

                // 性能优化
                this.optimizeLargeJson(value);

                this.$message.success('压缩成功');
            } catch (e) {
                this.$message.error('JSON格式错误: ' + e.message);
            } finally {
                this.isProcessing = false;
            }
        },

        handleCopy(e) {
            if (!this.formModel.result) {
                this.$message.warning('没有可复制的内容');
                return;
            }

            this.$clipboard(this.formModel.result, e);
            // this.$message.success('已复制到剪贴板');
        },

        handleClear() {
            this.$set(this.formModel, 'content', '');
            this.$set(this.formModel, 'result', '');
            this.$set(this.formModel, 'resultJson', null);
            this.searchText = '';
            this.$message.info('已清空');
        },

        // 性能优化：大JSON处理
        optimizeLargeJson(jsonData) {
            // 对于大型JSON，可以考虑分块处理
            const jsonString = JSON.stringify(jsonData);
            if (jsonString.length > 100000) { // 100KB
                // this.$message.info('检测到大型JSON，正在优化显示...');
                // 默认只展开前3层
                this.expandLevel = 3;
            }
        },

        // 复制JSON结果
        handleCopyResult(e) {
            if (!this.formModel.result) {
                this.$message.warning('没有可复制的内容');
                return;
            }

            this.$clipboard(this.formModel.result, e);
            this.$message.success('已复制到剪贴板');
        },

        // 全部展开
        expandAll() {
            this.expandLevel = 999; // 设置一个很大的数字来展开所有层级
            this.$message.info('已全部展开');
        },

        // 全部收起
        collapseAll() {
            this.expandLevel = 0;
            this.$message.info('已全部收起');
        },

    },
}
</script>

<style scoped>

.json-formatter-container {
    display: flex;
    width: 100%;
    gap: 12px;
    padding: 0 0 8px 0;
    overflow: hidden;
    align-items: stretch;
}

.left-panel {
    flex: 0 0 42%;
    min-width: 300px;
    display: flex;
    flex-direction: column;
}

.right-panel {
    flex: 0 0 58%;
    flex: 1;
    min-width: 400px;
    display: flex;
    flex-direction: column;
}

.box-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.box-card .el-card__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;
    overflow: hidden;
}

.json-input {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    line-height: 1.5;
}

.json-viewer-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    height: 660px;
}

.json-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.json-scroll-wrapper {
    flex: 1;
    overflow: auto;
    max-height: 100%;
    height: 0; /* 强制flex子元素遵守高度限制 */
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    background-color: #fafafa;
    padding: 12px;
    position: relative;
}

.json-scroll-wrapper::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.json-scroll-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.json-scroll-wrapper::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.json-scroll-wrapper::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 400px;
    color: #909399;
    font-size: 16px;
    background-color: #fafbfc;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    flex: 1;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

/* JSON美化样式优化 */
.json-pretty {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    line-height: 1.6;
    color: #333;
    height: 100% !important;
    max-height: 100% !important;
    overflow: auto !important;
}

.json-pretty .json-key {
    color: #0066cc;
    font-weight: 600;
}

.json-pretty .json-string {
    color: #008000;
}

.json-pretty .json-number {
    color: #ff6600;
}

.json-pretty .json-boolean {
    color: #cc00cc;
    font-weight: 600;
}

.json-pretty .json-null {
    color: #999;
    font-style: italic;
}

/* 头部操作按钮样式 */
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-actions .search-input {
    flex-shrink: 0;
}

.header-actions .el-button {
    flex-shrink: 0;
}

/* 按钮组样式 */
.header-actions .el-button + .el-button {
    margin-left: 0;
}

</style>
